import { ZOHO_SUBJECT_EMOJI_ALLOWLIST } from './zohoEmojiInventory';

export const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, Number(value) || 0));

export { ZOHO_SUBJECT_EMOJI_CATEGORY_ITEMS, ZOHO_SUBJECT_EMOJI_ALLOWLIST, ZOHO_SUBJECT_EMOJI_ALLOWLIST_VERSION, ZOHO_SUBJECT_EMOJI_GROUPS } from './zohoEmojiInventory';

const zohoEmojiSet = new Set(ZOHO_SUBJECT_EMOJI_ALLOWLIST.map(item => item.value));
const zohoEmojiEntries = ZOHO_SUBJECT_EMOJI_ALLOWLIST.slice().sort((left, right) => right.value.length - left.value.length);
const isVariationSelector = codePoint => codePoint >= 0xfe00 && codePoint <= 0xfe0f;
const isPotentialEmojiCodePoint = codePoint => (
  (codePoint >= 0x2300 && codePoint <= 0x23ff) ||
  (codePoint >= 0x2600 && codePoint <= 0x27ff) ||
  (codePoint >= 0x2b00 && codePoint <= 0x2bff) ||
  codePoint === 0x3030 || codePoint === 0x303d || codePoint === 0x3297 || codePoint === 0x3299
);

export const validateZohoSubject = subject => {
  if (typeof subject !== 'string') return { valid: false, incompatibleCharacters: [] };

  const incompatibleCharacters = [];
  const seenCodePoints = {};
  const addIncompatibleCharacter = (character, codePoint) => {
    const key = `U+${codePoint.toString(16).toUpperCase()}`;
    if (seenCodePoints[key]) return;
    seenCodePoints[key] = true;
    incompatibleCharacters.push({ character, codePoint: key });
  };

  for (let index = 0; index < subject.length;) {
    const exactEntry = zohoEmojiEntries.find(item => subject.slice(index, index + item.value.length) === item.value);
    if (exactEntry) {
      index += exactEntry.value.length;
      continue;
    }
    const codePoint = subject.codePointAt(index);
    const character = String.fromCodePoint(codePoint);
    const codeUnit = subject.charCodeAt(index);
    const unpairedSurrogate = codePoint === codeUnit && codeUnit >= 0xd800 && codeUnit <= 0xdfff;
    if (unpairedSurrogate) addIncompatibleCharacter(subject[index], codePoint);
    else if (isVariationSelector(codePoint) || codePoint === 0x20e3) addIncompatibleCharacter(character, codePoint);
    else if ((codePoint > 0xffff || isPotentialEmojiCodePoint(codePoint)) && !zohoEmojiSet.has(character)) addIncompatibleCharacter(character, codePoint);
    else if (codePoint === 0x200d) addIncompatibleCharacter(character, codePoint);
    index += character.length;
  }

  return { valid: incompatibleCharacters.length === 0, incompatibleCharacters };
};

export const insertAtSelection = (value, insertion, selectionStart, selectionEnd = selectionStart) => {
  const source = String(value || '');
  const start = Math.max(0, Math.min(Number(selectionStart) || 0, source.length));
  const end = Math.max(start, Math.min(Number(selectionEnd) || start, source.length));
  return { value: `${source.slice(0, start)}${insertion}${source.slice(end)}`, cursor: start + String(insertion || '').length };
};

export const inferAudienceSource = (audienceIds = [], lists = [], segments = []) => {
  const listIds = new Set(lists.map(item => item.id));
  const segmentIds = new Set(segments.map(item => item.id));
  const listMatches = audienceIds.filter(id => listIds.has(id));
  const segmentMatches = audienceIds.filter(id => segmentIds.has(id));
  const unresolvedIds = audienceIds.filter(id => !listIds.has(id) && !segmentIds.has(id));
  return { kind: segmentMatches.length && !listMatches.length ? 'segment' : 'list', unresolvedIds, mixed: Boolean(listMatches.length && segmentMatches.length), listMatches, segmentMatches };
};

export const audienceItemsOfKind = (items = [], kind) => items.filter(item => item && item.kind === kind);

export const audienceSelectionError = ({ unresolvedIds = [], mixed = false } = {}) => mixed ? 'No se pueden mezclar listas y segmentos en una misma campaña.' : (unresolvedIds.length ? 'No pudimos resolver uno o más destinatarios guardados. Volvé a cargar el catálogo antes de publicar.' : '');

export const ARGENTINA_TIME_ZONE = 'America/Argentina/Buenos_Aires';

const wallClockFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: ARGENTINA_TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
});

const partsForInstant = instant => wallClockFormatter.formatToParts(new Date(instant)).reduce((parts, part) => {
  if (part.type !== 'literal') parts[part.type] = Number(part.value);
  return parts;
}, {});

const utcTimestamp = ({ year, month, day, hour, minute }) => {
  const timestamp = Date.UTC(year, month - 1, day, hour, minute);
  const date = new Date(timestamp);
  date.setUTCFullYear(year);
  return date.getTime();
};

export const wallClockToUtcIso = value => {
  if (typeof value !== 'string' || !/^[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}$/.test(value)) return null;
  const [, year, month, day, hour, minute] = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  const wallClock = { year: Number(year), month: Number(month), day: Number(day), hour: Number(hour), minute: Number(minute) };
  if (wallClock.month < 1 || wallClock.month > 12 || wallClock.hour > 23 || wallClock.minute > 59) return null;
  const dayLimit = new Date(Date.UTC(wallClock.year, wallClock.month, 0)).getUTCDate();
  if (wallClock.day < 1 || wallClock.day > dayLimit) return null;

  const nominal = utcTimestamp(wallClock);
  let instant = nominal;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const zoneParts = partsForInstant(instant);
    instant = nominal - (utcTimestamp(zoneParts) - instant);
  }
  const roundTrip = partsForInstant(instant);
  const matches = ['year', 'month', 'day', 'hour', 'minute'].every(part => roundTrip[part] === wallClock[part]);
  return matches ? new Date(instant).toISOString() : null;
};

export const utcIsoToWallClock = value => {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/.test(value)) return null;
  const instant = new Date(value);
  if (Number.isNaN(instant.getTime())) return null;
  const parts = partsForInstant(instant.getTime());
  const year = String(parts.year);
  return year.length === 4
    ? `${year}-${String(parts.month).padStart(2, '0')}-${String(parts.day).padStart(2, '0')}T${String(parts.hour).padStart(2, '0')}:${String(parts.minute).padStart(2, '0')}`
    : null;
};

export const normaliseArea = area => {
  if (!area) return null;
  if (area.x !== undefined) {
    const x = clamp(area.x);
    const y = clamp(area.y);
    const width = clamp(area.width);
    const height = clamp(area.height);
    const boundedWidth = Math.min(width, 1 - x);
    const boundedHeight = Math.min(height, 1 - y);
    return boundedWidth > 0 && boundedHeight > 0 ? { ...area, x, y, width: boundedWidth, height: boundedHeight, shape: area.shape || 'rectangle' } : null;
  }
  const width = Number(area.imageWidth || area.naturalWidth || 0);
  const height = Number(area.imageHeight || area.naturalHeight || 0);
  if (!width || !height) return null;
  const x1 = Number(area.x1) || 0;
  const x2 = Number(area.x2) || 0;
  const y1 = Number(area.y1) || 0;
  const y2 = Number(area.y2) || 0;
  const left = clamp(Math.min(x1, x2) / width);
  const top = clamp(Math.min(y1, y2) / height);
  const right = clamp(Math.max(x1, x2) / width);
  const bottom = clamp(Math.max(y1, y2) / height);
  return right > left && bottom > top ? {
    title: area.title || '', href: area.href || '', shape: area.shape || 'rectangle',
    x: left, y: top, width: right - left, height: bottom - top
  } : null;
};

export const isMeaningfulArea = (area, minimum = 0.01) => Boolean(area && area.width >= minimum && area.height >= minimum);

export const createPresetArea = (area, preset) => {
  if (!isMeaningfulArea(area) || !preset) return null;
  return normaliseArea({ ...area, title: preset.title, href: preset.href });
};

export const parseAreas = (value, imageWidth, imageHeight) => {
  try {
    const parsed = Array.isArray(value) ? value : JSON.parse(value || '[]');
    return parsed.map(area => normaliseArea({ ...area, imageWidth, imageHeight })).filter(Boolean);
  } catch (error) {
    return [];
  }
};

export const safeUrl = value => {
  try {
    const url = new URL(String(value || '').trim());
    return ['http:', 'https:'].includes(url.protocol) ? url.href : null;
  } catch (error) { return null; }
};

export const safeAreaUrl = value => {
  const stringValue = String(value || '').trim();
  if (/^mailto:[^\s@]+@[^\s@]+\.[^\s@]+$/i.test(stringValue)) return stringValue;
  if (!/^https?:\/\//i.test(stringValue)) return null;
  return safeUrl(stringValue);
};

export const deliverySafety = (delivery, state, localPending = false) => {
  const lastError = delivery && delivery.lastError;
  const syncStatus = delivery && delivery.syncStatus;
  const requiresReconcile = syncStatus === 'unknown' || state === 'unknown' || Boolean(lastError && (lastError.outcomeUnknown || lastError.reconcileRequired));
  const missingExternalKey = state === 'unknown' && Boolean(delivery && delivery.hasExternalReference === false);
  const pendingChanges = state !== 'unpublished' && (localPending || syncStatus === 'out_of_sync' || state === 'out_of_sync' || Boolean(delivery && delivery.stale));
  const syncing = syncStatus === 'syncing' || state === 'syncing';
  return {
    requiresReconcile,
    sendingBlocked: requiresReconcile || pendingChanges || syncing,
    message: requiresReconcile
      ? (missingExternalKey ? 'La entrega quedó sin identificador remoto. Reconciliá para habilitar una nueva publicación.' : 'No se pudo confirmar el resultado. Reconciliá antes de continuar.')
      : pendingChanges
        ? 'Hay cambios pendientes. Actualizá el borrador antes de enviarlo.'
        : syncing ? 'La entrega se está sincronizando. Esperá antes de continuar.' : ''
  };
};

export const evidenceReady = delivery => {
  const evidence = delivery && delivery.evidence;
  return Boolean(delivery && delivery.syncStatus === 'synchronized' && evidence && evidence.desiredVersion != null && evidence.confirmedVersion != null && evidence.confirmedEvidenceId);
};

export const htmlNeedsPersist = (persistedHtml, generatedHtml) => persistedHtml != null && Boolean(generatedHtml) && persistedHtml !== generatedHtml;

export const buildCampaignSavePayload = ({ campaignName, campaignUrl, areas, imageData, htmlContent }) => ({
  name: String(campaignName || '').trim(), campaign_link: String(campaignUrl || '').trim(),
  areas_map: JSON.stringify(areas || []), campaign_image: imageData, html: htmlContent
});

export const createdCampaignId = response => response && response.campaign && response.campaign._id;

export const deliverySuccessMessage = (action, result) => action === 'reconcile' && result && result.operation && result.operation.outcome === 'confirmed' && result.delivery && result.delivery.syncStatus === 'synchronized'
  ? 'Borrador reconciliado.'
  : 'Operación aceptada. Actualizando estado…';

export const isPostSendState = state => state === 'sent';

export const normalizeRecipientOffset = offset => Number.isInteger(offset) && offset >= 0 ? offset : 0;

export const reviewImageSource = ({ embeddedImageUrl, localImageUrl }) => embeddedImageUrl || localImageUrl || '';

export const safeImageUrl = value => {
  const stringValue = String(value || '').trim();
  if (/^data:image\/(gif|jpe?g|png|webp);base64,[a-z0-9+/=]+$/i.test(stringValue)) return stringValue;
  return safeUrl(stringValue);
};

export const rewriteAttachmentUrl = (value, publicAssetOrigin) => {
  try {
    const source = new URL(String(value || '').trim());
    const origin = new URL(String(publicAssetOrigin || '').trim());
    if (!['http:', 'https:'].includes(source.protocol) || !['http:', 'https:'].includes(origin.protocol) || !source.pathname.startsWith('/attachment/read/')) return value;
    return `${origin.origin}${source.pathname}${source.search}${source.hash}`;
  } catch (error) { return value; }
};

export const escapeHtml = value => String(value == null ? '' : value)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

export const buildCampaignHtml = ({ imageUrl, campaignName, campaignUrl, areas = [], emailSafe = true, imageWidth = 600, imageHeight = 1100 }) => {
  const safeCampaignUrl = safeUrl(campaignUrl);
  const safeImage = safeImageUrl(imageUrl);
  if (!safeImage || !safeCampaignUrl || !String(campaignName || '').trim()) return '';
  const normalizedAreas = areas.map(normaliseArea).filter(Boolean);
  const links = normalizedAreas.map(area => {
    const href = safeAreaUrl(area.href);
    if (!href) return '';
    const externalAttributes = href.toLowerCase().startsWith('mailto:') ? '' : ' target="_blank" rel="noopener"';
    return `<a href="${escapeHtml(href)}"${externalAttributes} aria-label="${escapeHtml(area.title)}" class="small-link" style="left:${(area.x * 100).toFixed(4)}%;top:${(area.y * 100).toFixed(4)}%;width:${(area.width * 100).toFixed(4)}%;height:${(area.height * 100).toFixed(4)}%;"></a>`;
  }).join('');
  const styles = emailSafe
    ? 'margin:0 auto;max-width:100%;position:relative;display:inline-block;line-height:0;'
    : 'position:relative;display:inline-block;line-height:0;';
  const emailCss = emailSafe ? '<style>.image-container{position:relative;display:inline-block}.big-link{display:block;position:absolute;width:100%;height:100%;top:0;left:0;z-index:1}.small-link{position:absolute;display:block;color:transparent;z-index:2}.image-container img{display:block;height:auto;width:100%}</style>' : '';
  const image = emailSafe
    ? `<div class="image-container"><a href="${escapeHtml(safeCampaignUrl)}" target="_blank" rel="noopener" class="big-link"></a><img src="${escapeHtml(safeImage)}" alt="${escapeHtml(campaignName)}">${links}</div>`
    : `<div style="${styles}"><a href="${escapeHtml(safeCampaignUrl)}" target="_blank" rel="noopener" style="display:block;line-height:0"><img src="${escapeHtml(safeImage)}" alt="${escapeHtml(campaignName)}" style="display:block;width:100%;height:auto;border:0"></a>${links}</div>`;
  return `<!doctype html><html lang="es"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>${escapeHtml(campaignName)}</title>${emailCss}</head><body style="margin:0;padding:0;text-align:center;background:#fff"><center><p style="font:11px Arial,sans-serif;line-height:16px;margin:0;padding:10px">Si no podés visualizar correctamente el contenido, <a href="${escapeHtml(safeCampaignUrl)}" target="_blank" rel="noopener">hacé clic acá</a>.</p>${image}</center></body></html>`;
};
